import { usePermissions } from '../permissions/usePermissions';
import { useSelector } from 'react-redux';
import { can as hasGrant } from '../permissions/policy';
import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Card, Button, Modal, Form, Input, Space, Switch, message } from 'antd';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { SearchOutlined, PlusOutlined, ReloadOutlined, ColumnWidthOutlined } from '@ant-design/icons';
import { 
  DeliveryModeAPI, 
  PaymentModeAPI, 
  CountryAPI, 
  CityAPI, 
  BankAPI 
} from '../models/listsAPI';

const autosizeOptions = { includeHeaders: true, includeOutliers: true, expand: false };
const normalizeName = (value) => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const ListCrud = ({ title, api, moduleId, hasDesc = false }) => {
  const { can } = usePermissions(moduleId);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const gridApiRef = useGridApiRef();
  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const filteredItems = useMemo(() => items.filter((item) => normalizeName(item.name).includes(normalizeName(search.trim()))), [items, search]);

  useEffect(() => {
    if (loading) return undefined;
    const frame = requestAnimationFrame(() => {
      gridApiRef.current.autosizeColumns(autosizeOptions);
    });
    return () => cancelAnimationFrame(frame);
  }, [gridApiRef, loading, filteredItems, paginationModel]);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await api.getAll();
      setItems(data);
    } catch (e) {
      message.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(() => {
    const baseCols = [
      { headerName: 'ID', field: 'id', minWidth: 60 },
      { headerName: 'Name', field: 'name', minWidth: 100 },
    ];
    
    if (hasDesc) {
      baseCols.push({
        headerName: 'Remarque livraison / ventilé',
        field: 'desc',
        minWidth: 120,
        renderCell: ({ value }) => value || '-'
      });
    }
    
    baseCols.push(
      { 
        headerName: 'Active',
        field: 'active',
        type: 'boolean',
        minWidth: 90,
        renderCell: ({ value: v, row: record }) => (
          <Switch 
            checked={!!v} disabled={!can('update')}
            onChange={async (checked) => {
              try {
                await api.update(record.id, { active: checked });
                setItems((prev) => prev.map((r) => r.id === record.id ? { ...r, active: checked } : r));
              } catch (e) {
                message.error(e?.response?.data?.message || e.message);
              }
            }}
          />
        )
      },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filterable: false,
        minWidth: 160,
        renderCell: ({ row: record }) => (
          <Space>
            {can('update') && <Button size="small" onClick={() => {
              setEditing(record);
              const values = { name: record.name, active: record.active };
              if (hasDesc) values.desc = record.desc;
              form.setFieldsValue(values);
              setIsModalOpen(true);
            }}>Edit</Button>}
            {can('delete') && <Button size="small" danger onClick={async () => {
              Modal.confirm({
                title: `Delete ${record.name}?`,
                onOk: async () => {
                  try {
                    await api.remove(record.id);
                    setItems((prev) => prev.filter((r) => r.id !== record.id));
                    message.success('Deleted');
                  } catch (e) {
                    message.error(e?.response?.data?.message || e.message);
                  }
                }
              });
            }}>Delete</Button>}
          </Space>
        )
      }
    );
    
    return baseCols;
  }, [api, form, hasDesc, can]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true });
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        const updated = await api.update(editing.id, values);
        setItems((prev) => prev.map((r) => r.id === editing.id ? updated.data || updated : r));
        message.success('Updated');
      } else {
        const created = await api.create(values);
        setItems((prev) => [ ...(prev || []), created.data || created ]);
        message.success('Created');
      }
      setIsModalOpen(false);
      setEditing(null);
    } catch (e) {
      if (e?.errorFields) return; // form errors
      message.error(e?.response?.data?.message || e.message);
    }
  };

  return (
    <Card style={{ marginTop: 16, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Rechercher par nom"
          aria-label="Rechercher par nom"
          allowClear
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          style={{ width: 300, maxWidth: '100%' }}
        />
        <Space wrap>
          {can('create') && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add {title}</Button>}
          <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>Refresh</Button>
          <Button icon={<ColumnWidthOutlined />} onClick={() => gridApiRef.current.autosizeColumns(autosizeOptions)}>Ajuster les colonnes</Button>
        </Space>
      </div>
      <DataGrid
        apiRef={gridApiRef}
        rows={filteredItems}
        columns={columns}
        loading={loading}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        autosizeOnMount
        autosizeOptions={autosizeOptions}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50, 100]}
        localeText={{ noRowsLabel: 'Aucune donnée', noResultsOverlayLabel: 'Aucun résultat' }}
        sx={{
          backgroundColor: '#fff',
          '& .MuiDataGrid-columnHeader': { backgroundColor: '#fafafa' },
          '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
          '& .MuiDataGrid-row:hover': { backgroundColor: '#f5f5f5' },
          '& .MuiDataGrid-columnSeparator': { color: '#d9d9d9' },
          '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #f0f0f0' },
        }}
      />

      <Modal 
        open={isModalOpen} 
        onOk={handleOk} 
        onCancel={() => { setIsModalOpen(false); setEditing(null); }}
        title={editing ? `Edit ${title}` : `Add ${title}`}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
            <Input />
          </Form.Item>
          {hasDesc && (
            <Form.Item name="desc" label="Remarque livraison / ventilé" extra="Imprimée sur les ventilés des devis utilisant ce mode de livraison (MODELIV).">
              <Input.TextArea rows={2} placeholder="Ex. DTR — MER & SAM (L2 ou L3)" />
            </Form.Item>
          )}
          <Form.Item name="active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

const ListsManagementPage = () => {
  const user = useSelector(state => state.user);
  return (
    <Tabs
      items={[
        { permissionModule: 'deliveryModes', key: 'delivery', label: 'Delivery Modes', children: <ListCrud title="Delivery Mode" moduleId="deliveryModes" api={DeliveryModeAPI} hasDesc /> },
        { permissionModule: 'paymentModes', key: 'payment', label: 'Payment Modes', children: <ListCrud title="Payment Mode" moduleId="paymentModes" api={PaymentModeAPI} /> },
        { permissionModule: 'countries', key: 'countries', label: 'Countries', children: <ListCrud title="Country" moduleId="countries" api={CountryAPI} /> },
        { permissionModule: 'cities', key: 'cities', label: 'Cities', children: <ListCrud title="City" moduleId="cities" api={CityAPI} /> },
        { permissionModule: 'banks', key: 'banks', label: 'Banks', children: <ListCrud title="Bank" moduleId="banks" api={BankAPI} /> },
      ].filter(item => hasGrant(user, item.permissionModule))}
    />
  );
};

export default ListsManagementPage;

